import React, { useEffect, useRef, useState } from 'react'
import ReCaptcha, { type ReCAPTCHAProps } from 'react-google-recaptcha'
import { GoogleReCaptcha } from 'react-google-recaptcha-v3'
import styled from 'styled-components'

import { QA } from '@hozana/tests/constants'

export type TCaptchaProps = Omit<ReCAPTCHAProps, 'sitekey'> & {
  value?: string
  scale?: number
  lang?: string
  onChangeV2?: ((token: string) => void) | undefined
  onChangeV3?: ((token: string) => void) | undefined
  showV2?: boolean
}

export const Wrapper = styled.div<Pick<TCaptchaProps, 'scale'>>`
  overflow: hidden;

  iframe {
    transform-origin: 0 0;
    transform: scale(${(props) => props.scale || 1});
  }
`

/**
 * Captcha
 * This component handle both v2 and v3 recaptcha
 * The behavior is the following :
 * Initially v3 is used, if the user obtains a score below the threshold, v2 is used
 * Throught v2 user can solve the puzzle to be considered as a human
 */
export const Captcha: TFCC<TCaptchaProps> = ({
  value,
  scale,
  onChangeV2,
  onChangeV3,
  showV2,
  lang = 'fr',
  ...otherProps
}) => {
  const captchaV2 = useRef(null)

  const [refreshCaptcha, setRefreshCaptcha] = useState(false)

  useEffect(() => {
    if (!value && captchaV2.current) {
      captchaV2.current.reset()
      // Only the value changes does matter, not the value itself
      setRefreshCaptcha((v) => !v)
    }
  }, [value])

  return (
    <>
      {/* ReCaptcha v3 */}
      {CONF_KEYS.RECAPTCHA_API_V3_KEY != null && (
        <GoogleReCaptcha refreshReCaptcha={refreshCaptcha} data-testid={QA.AUTH.CAPTCHA} onVerify={onChangeV3} />
      )}
      <Wrapper scale={scale}>
        {(showV2 || !CONF_KEYS.RECAPTCHA_API_V3_KEY) && (
          /* ReCaptcha v2 */
          <ReCaptcha
            data-testid={QA.AUTH.CAPTCHA}
            ref={captchaV2}
            hl={lang}
            size="normal"
            sitekey={CONF_KEYS.RECAPTCHA_API_V2_KEY}
            onChange={onChangeV2}
            {...otherProps}
          />
        )}
      </Wrapper>
    </>
  )
}
